/**
 * Project Command Service
 * 
 * Application service for project write operations following CQRS pattern.
 * Encapsulates business use cases and orchestrates domain operations.
 */

import { injectable, inject } from 'inversify';
import { DDD_SYMBOLS } from '../../../../infrastructure/di/DDD_SYMBOLS';
import { Project, ProjectProperties } from '../../domain/entities/Project';
import type { IProjectRepository } from '../../../meta-model-design/domain/ports/IProjectRepository';
import { CreateProjectPayload } from '../commands/CreateProjectCommand';

export interface CreateProjectResult {
  projectId: string;
  success: boolean;
  message: string;
}

@injectable()
export class ProjectCommandService {
  constructor(
    @inject(DDD_SYMBOLS.IProjectRepository) private projectRepository: IProjectRepository
  ) {}

  async createProject(payload: CreateProjectPayload): Promise<CreateProjectResult> {
    // Check if project with same name already exists
    const existingProject = await this.projectRepository.findByName(payload.name);
    if (existingProject) {
      throw new Error(`Project with name "${payload.name}" already exists`);
    }

    // Create project entity with business logic validation
    const project = Project.create({
      name: payload.name,
      description: payload.description || '',
      domainId: payload.domainId,
      boundedContextId: payload.boundedContextId || undefined,
      metadata: payload.metadata || {}
    });

    // Persist to repository
    await this.projectRepository.save(project);

    return {
      projectId: project.id,
      success: true,
      message: `Project "${payload.name}" created successfully with ID ${project.id}`
    };
  }

  async deleteProject(projectId: string): Promise<void> {
    const project = await this.projectRepository.findById(projectId);
    if (!project) {
      throw new Error(`Project not found: ${projectId}`);
    }

    await this.projectRepository.deleteById(projectId);
  }
}